import React, { JSX } from 'react';
import RefreshButton from '../../shared/RefreshButton';
import type { ExplorerDataset } from '../types/DataExplorerTypes';

type DataExplorerControlsProps = {
  isAdmin: boolean;
  dataset: ExplorerDataset;
  onDatasetChange: (dataset: ExplorerDataset) => void;
  deviceIdSearch: string;
  onDeviceIdSearchChange: (value: string) => void;
  athleteAssignmentsRaceSearch: string;
  onAthleteAssignmentsRaceSearchChange: (value: string) => void;
  explorerBusy: boolean;
  onRefresh: () => void;
  onDownloadCsv: () => void;
  hasAnyRows: boolean;
};

const DATASET_INFO: Record<ExplorerDataset, { what: string; actions: string }> = {
  events: {
    what: 'Historical event records with status and metadata.',
    actions: 'Review event lifecycle history and export rows to CSV.',
  },
  requests: {
    what: 'Command/request audit records sent to devices.',
    actions: 'Inspect request payloads and timing details, then export filtered data.',
  },
  users: {
    what: 'Read-only user directory from Cognito/user APIs.',
    actions: 'Browse user identities, roles/groups, and export the current dataset.',
  },
  deviceMappings: {
    what: 'Canonical device mappings stored in DynamoDB.',
    actions: 'Read-only. Search by device ID and export to CSV. Add/remove devices from the Device List page.',
  },
  deviceNotes: {
    what: 'Append-only device notes in S3 (per device).',
    actions:
      'Refresh loads all notes (paginated) or narrows to one device if you type a device ID. Search filters loaded rows.',
  },
  deviceLifecycle: {
    what: 'Append-only device lifecycle events in DynamoDB (lost / decommissioned / restore).',
    actions:
      'Loads the first page immediately, then pulls more pages until done (cap applies). Filter with the search box. Set DEVICE_LIFECYCLE_TABLE in .env.',
  },
  eventRaces: {
    what: 'Per-event race rows (status, name, configuration pointers) in DynamoDB.',
    actions:
      'Admin scan of the live event-races table. Filter by event ID, race ID, or name substring. Set EVENT_RACES_TABLE in .env.',
  },
  eventDeviceAssignments: {
    what: 'Device-to-event assignment records (active/history depending on event status).',
    actions: 'Load by event ID or aggregate view, validate assignments, and export.',
  },
  eventRaceDeviceAssignmentHistory: {
    what: 'Archived race-device assignment rows in DynamoDB (same table as the AWS console).',
    actions:
      'Paginated Dynamo scan; search filters all loaded rows. Set RACE_DEVICES_HISTORY_TABLE in .env.',
  },
  eventAthleteAssignments: {
    what: 'Athlete assignment files and CSV rows per race.',
    actions:
      'Leave event blank for all events (file metadata). Event only: per-race summary rows. Add race ID for full CSV rows.',
  },
  firmwareVersions: {
    what: 'Available firmware versions for rear and front device roles.',
    actions: 'Browse firmware artifacts and export the list as CSV.',
  },
};

export default function DataExplorerControls(props: DataExplorerControlsProps): JSX.Element {
  const {
    isAdmin,
    dataset,
    onDatasetChange,
    deviceIdSearch,
    onDeviceIdSearchChange,
    athleteAssignmentsRaceSearch,
    onAthleteAssignmentsRaceSearchChange,
    explorerBusy,
    onRefresh,
    onDownloadCsv,
    hasAnyRows,
  } = props;

  return (
    <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-b from-sky-50 via-white to-white dark:from-slate-800 dark:via-slate-900 dark:to-slate-950">
      <div className="grid grid-cols-1 gap-2">
        <label className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">Dataset</label>
        <select
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm"
          value={dataset}
          disabled={explorerBusy}
          onChange={(e) => onDatasetChange(e.target.value as ExplorerDataset)}
        >
          <option value="events">Event History</option>
          <option value="requests">Requests</option>
          {isAdmin && <option value="users">Users (Cognito)</option>}
          <option value="deviceMappings">Device Mappings (DynamoDB)</option>
          {isAdmin && <option value="deviceNotes">Device Notes (S3)</option>}
          {isAdmin && <option value="deviceLifecycle">Device Lifecycle (DynamoDB)</option>}
          {isAdmin && <option value="eventRaces">Event Races (DynamoDB)</option>}
          <option value="eventDeviceAssignments">Event Device Assignments</option>
          {isAdmin && (
            <option value="eventRaceDeviceAssignmentHistory">
              Event Race Device Assignment History (DynamoDB)
            </option>
          )}
          <option value="eventAthleteAssignments">Event Athlete Assignments</option>
          <option value="firmwareVersions">Firmware Versions</option>
        </select>
      </div>

      <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          {dataset === 'deviceMappings' && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[240px]"
                placeholder="Search device ID..."
                value={deviceIdSearch}
                onChange={(e) => onDeviceIdSearchChange(e.target.value)}
              />
            </div>
          )}

          {dataset === 'deviceNotes' && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[240px]"
                placeholder="Optional: filter to one device ID…"
                value={deviceIdSearch}
                onChange={(e) => onDeviceIdSearchChange(e.target.value)}
              />
              <span className="text-xs text-gray-500 dark:text-slate-400 self-center max-w-md">
                Leave blank to load notes across all devices (admin). Search below filters the loaded rows.
              </span>
            </div>
          )}

          {dataset === 'deviceLifecycle' && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[240px]"
                placeholder="Filter by device ID..."
                value={deviceIdSearch}
                onChange={(e) => onDeviceIdSearchChange(e.target.value)}
              />
            </div>
          )}

          {dataset === 'eventRaces' && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[280px]"
                placeholder="Filter by event ID, race ID, or name..."
                value={deviceIdSearch}
                onChange={(e) => onDeviceIdSearchChange(e.target.value)}
              />
              <span className="text-xs text-gray-500 dark:text-slate-400 self-center max-w-md">
                Loads on Refresh; optional filter narrows rows client-side.
              </span>
            </div>
          )}

          {dataset === 'eventDeviceAssignments' && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[240px]"
                placeholder="Event ID..."
                value={deviceIdSearch}
                onChange={(e) => onDeviceIdSearchChange(e.target.value)}
              />
              <span className="text-xs text-gray-500 dark:text-slate-400 self-center">
                Load assignments for this event ID (leave blank to load all where supported).
              </span>
            </div>
          )}

          {dataset === 'eventRaceDeviceAssignmentHistory' && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[280px]"
                placeholder="Filter by device ID (optional)..."
                value={deviceIdSearch}
                onChange={(e) => onDeviceIdSearchChange(e.target.value)}
              />
              <span className="text-xs text-gray-500 dark:text-slate-400 self-center max-w-md">
                Table loads on Refresh; use the box to narrow rows by device ID.
              </span>
            </div>
          )}

          {dataset === 'eventAthleteAssignments' && (
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[240px]"
                placeholder="Event ID..."
                value={deviceIdSearch}
                onChange={(e) => onDeviceIdSearchChange(e.target.value)}
              />
              <input
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[200px]"
                placeholder="Race ID..."
                value={athleteAssignmentsRaceSearch}
                onChange={(e) => onAthleteAssignmentsRaceSearchChange(e.target.value)}
              />
              <span className="text-xs text-gray-500 dark:text-slate-400 self-center max-w-xl">
                Race ID loads expanded CSV rows for that race. Event-only Refresh lists per-race file metadata for the
                event.
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col items-start lg:items-end gap-2">
          <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2">
            <div className="max-w-[420px] rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[11px] text-gray-700 dark:text-slate-300">
              <span className="font-semibold text-gray-800 dark:text-slate-200">Info:</span> {DATASET_INFO[dataset].what}
            </div>
            <RefreshButton
              busy={explorerBusy}
              onClick={onRefresh}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50 shadow-sm"
              busyText="Refreshing..."
            />
            <button
              type="button"
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50 shadow-sm"
              onClick={onDownloadCsv}
              disabled={!hasAnyRows}
            >
              Download CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
